import { decode } from 'he'

import { ProjectType, type ProjectTypeValue } from '@/constants/projectTypes'
import {
  DRASTIC_DELETION_MAX_REMAINING_RATIO,
  DRASTIC_DELETION_MIN_EXISTING_WORDS,
} from '@/constants/chapterContentDeletion'
import isTipTapDoc from '@/utils/isTipTapDoc'

function countWords(text: string): number {
  return text.split(/\s+/).filter(Boolean).length
}

export function countWordsInHtmlContent(content: string): number {
  const text = (content || '')
    .replace(/<(\/?(p|div|br|li|h[1-6]|blockquote))[^>]*>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return countWords(decode(text))
}

function sumWordsFromScreenplayBlockContent(content: unknown): number {
  if (!Array.isArray(content)) return 0
  return content.reduce((sum: number, item: unknown) => {
    const text = typeof item === 'object' && item !== null && 'text' in item
      ? String((item as { text?: unknown }).text ?? '')
      : ''
    return sum + countWords(text)
  }, 0)
}

export function countWordsInScreenplayJson(jsonContent: unknown): number {
  if (!jsonContent || typeof jsonContent !== 'object') return 0
  const root = jsonContent as { content?: unknown }
  if (!Array.isArray(root.content)) return 0
  return root.content.reduce((sum: number, item: unknown) => {
    const blockContent = typeof item === 'object' && item !== null && 'content' in item
      ? (item as { content?: unknown }).content
      : undefined
    return sum + sumWordsFromScreenplayBlockContent(blockContent)
  }, 0)
}

export function collectTipTapTexts(node: unknown): string[] {
  if (!node || typeof node !== 'object') return []

  if (Array.isArray(node)) {
    return node.reduce((texts: string[], item) => texts.concat(collectTipTapTexts(item)), [])
  }

  const value = node as { type?: unknown; text?: unknown; content?: unknown }
  if (value.type === 'text' && typeof value.text === 'string' && value.text) {
    return [value.text]
  }

  if (Array.isArray(value.content)) {
    return collectTipTapTexts(value.content)
  }

  return []
}

export function countWordsInTipTapJson(jsonContent: unknown): number {
  return collectTipTapTexts(jsonContent).reduce(
    (sum, text) => sum + countWords(text),
    0,
  )
}

function chapterWordCount(
  chapter: { jsonContent?: unknown },
  projectType: ProjectTypeValue | string,
): number {
  if (projectType === ProjectType.SCREENPLAY) {
    return countWordsInScreenplayJson(chapter.jsonContent)
  }
  if (isTipTapDoc(chapter.jsonContent)) {
    return countWordsInTipTapJson(chapter.jsonContent)
  }
  return 0
}

export function isDrasticWordCountReduction(existingWords: number, newWords: number): boolean {
  if (existingWords < DRASTIC_DELETION_MIN_EXISTING_WORDS) return false
  if (newWords >= existingWords) return false
  return newWords <= existingWords * DRASTIC_DELETION_MAX_REMAINING_RATIO
}

export function isDrasticChapterContentDeletion(
  projectType: ProjectTypeValue | string,
  existingChapter: { jsonContent?: unknown },
  patch: { jsonContent?: object },
): boolean {
  if (patch.jsonContent === undefined) return false

  if (projectType === ProjectType.SCREENPLAY) {
    return isDrasticWordCountReduction(
      countWordsInScreenplayJson(existingChapter.jsonContent),
      countWordsInScreenplayJson(patch.jsonContent),
    )
  }

  return isDrasticWordCountReduction(
    chapterWordCount(existingChapter, projectType),
    countWordsInTipTapJson(patch.jsonContent),
  )
}
